import { useEffect, useState } from "react";
import { api, euro, formatDate, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { PaperPlaneTilt, DownloadSimple, Eye, Trash, FloppyDisk, FilePdf, PencilSimple, Plus, X } from "@phosphor-icons/react";
import { toast } from "sonner";

const STATUS_COLORS = {
  "Entwurf": "bg-slate-100 text-slate-700",
  "erstellt": "bg-blue-100 text-blue-800",
  "versendet": "bg-amber-100 text-amber-800",
  "bezahlt": "bg-emerald-100 text-emerald-800",
  "storniert": "bg-red-100 text-red-800",
};

const MONTHS_DE = ["Januar","Februar","März","April","Mai","Juni","Juli","August","September","Oktober","November","Dezember"];
const dePeriod = (a, b) => {
  if (!a || !b) return "";
  const [ay,am,ad] = a.split("-").map(Number);
  const [by,bm,bd] = b.split("-").map(Number);
  if (ay === by) return `${String(ad).padStart(2,"0")}. ${MONTHS_DE[am-1]} – ${String(bd).padStart(2,"0")}. ${MONTHS_DE[bm-1]} ${by}`;
  return `${String(ad).padStart(2,"0")}. ${MONTHS_DE[am-1]} ${ay} – ${String(bd).padStart(2,"0")}. ${MONTHS_DE[bm-1]} ${by}`;
};

export default function Invoices() {
  const [rows, setRows] = useState([]);
  const [preview, setPreview] = useState(null);
  const [editInv, setEditInv] = useState(null); // komplette Rechnungs-Bearbeitung
  const [sendPreview, setSendPreview] = useState(null); // { invoice, to, bcc, subject, html }
  const [editMode, setEditMode] = useState(false);
  const [activeTab, setActiveTab] = useState("invoice");   // "invoice" | "email"
  const [invEdit, setInvEdit] = useState(null);            // lokale Kopie der Rechnungsfelder
  const [savingInv, setSavingInv] = useState(false);
  const [pdfBust, setPdfBust] = useState(0);               // Cache-Buster nach Save
  const [counterInfo, setCounterInfo] = useState(null);
  const [counterInput, setCounterInput] = useState("");
  const load = () => api.get("/invoices").then(r => setRows(r.data));
  const loadCounter = () => api.get("/admin/invoice-counter").then(r => setCounterInfo(r.data)).catch(()=>{});
  useEffect(() => { load(); loadCounter(); }, []);

  const openSendPreview = async (inv) => {
    try {
      const r = await api.get(`/invoices/${inv.id}/preview-email`);
      setSendPreview({ invoice: inv, ...r.data });
      setInvEdit({
        guest_name: inv.guest_name || "",
        company: inv.company || "",
        attention: inv.attention || "",
        address: inv.address || "",
        invoice_date: inv.invoice_date || "",
        due_date: inv.due_date || "",
        description: inv.description || "",
        payment_note: inv.payment_note || "",
        paid_via_platform: inv.paid_via_platform ?? null,
      });
      setActiveTab("invoice");
      setEditMode(false);
      setPdfBust(Date.now());
    } catch (e) {
      toast.error(e.response?.data?.detail || "Vorschau konnte nicht geladen werden");
    }
  };

  const saveInvoiceIfDirty = async () => {
    if (!sendPreview || !invEdit) return true;
    const cur = sendPreview.invoice;
    const dirty = Object.keys(invEdit).some(k => (invEdit[k] ?? "") !== (cur[k] ?? ""));
    if (!dirty) return true;
    try {
      const { data: updated } = await api.patch(`/invoices/${cur.id}`, invEdit);
      const email = await api.get(`/invoices/${cur.id}/preview-email`);
      setSendPreview({ invoice: updated, ...email.data });
      setPdfBust(Date.now());
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Änderungen konnten nicht gespeichert werden — Versand abgebrochen");
      return false;
    }
  };

  const gotoEmailTab = async () => {
    if (await saveInvoiceIfDirty()) setActiveTab("email");
  };

  const saveInvoice = async () => {
    setSavingInv(true);
    try {
      const ok = await saveInvoiceIfDirty();
      if (ok) toast.success("Rechnung aktualisiert");
    } finally { setSavingInv(false); }
  };

  const confirmSend = async () => {
    if (!sendPreview) return;
    try {
      // FIX 1: Erst pending Rechnungs-Änderungen (Empfänger, Fälligkeit, Zahlungstext, etc.) speichern.
      if (!(await saveInvoiceIfDirty())) return;
      // FIX 2: Immer subject + html aus State senden, damit Änderungen (auch nach dem
      // Umschalten in die Vorschau) mitgesendet werden. Ohne diese Zeile wurde bei
      // editMode=false ein leeres Payload gesendet → Backend erzeugte Standardtext.
      const payload = {
        subject: sendPreview.subject,
        html: sendPreview.html,
      };
      const r = await api.post(`/invoices/${sendPreview.invoice.id}/send`, payload);
      const pdfOk = !!r.data?.pdf_attached;
      const via = r.data?.sent_via || "?";
      if (pdfOk) {
        toast.success(
          `✅ Rechnung mit PDF an ${sendPreview.to} versendet · Kopie an ${sendPreview.cc || sendPreview.bcc || "—"} (von info@mainspessartblick.de)`,
          { duration: 8000 }
        );
      } else {
        toast.warning(
          `⚠️ Rechnung an ${sendPreview.to} versendet, aber OHNE PDF-Anhang (via ${via}).`,
          { duration: 12000 }
        );
      }
      setSendPreview(null);
      setEditMode(false);
      load();
    } catch (e) {
      const status = e.response?.status;
      const detail = e.response?.data?.detail || "Versand fehlgeschlagen";
      if (status === 502) {
        toast.error(`❌ E-Mail-Versand fehlgeschlagen — ${detail}`, { duration: 15000 });
      } else {
        toast.error(detail);
      }
    }
  };

  const setCounter = async () => {
    const v = Number(counterInput);
    if (!Number.isFinite(v) || v < 0) {
      toast.error("Bitte eine ganze Zahl ≥ 0 eingeben");
      return;
    }
    if (!window.confirm(`Zähler auf ${v} setzen? Die NÄCHSTE Rechnung wird dann ${counterInfo?.year || new Date().getFullYear()}-${String(v+1).padStart(2,"0")}.`)) return;
    try {
      const r = await api.post("/admin/invoice-counter", { seq: v });
      setCounterInfo(r.data);
      setCounterInput("");
      toast.success(`Zähler gesetzt · nächste Rechnung: ${r.data.next_number}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Zähler konnte nicht gesetzt werden");
    }
  };

  const remove = async (inv) => {
    if (!window.confirm(`Rechnung ${inv.number} (${inv.guest_name}) wirklich löschen?\n\nDie Buchung bleibt erhalten, du kannst die Rechnung danach neu erstellen.`)) return;
    try {
      await api.delete(`/invoices/${inv.id}`);
      toast.success(`Rechnung ${inv.number} gelöscht`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Löschen fehlgeschlagen");
    }
  };

  return (
    <div className="space-y-6" data-testid="invoices-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold" style={{fontFamily:"'Work Sans',sans-serif"}}>Rechnungen</h1>
          <p className="text-muted-foreground mt-1">{rows.length} Rechnungen · deutsche Kleinunternehmer/Standardrechnung, 7% MwSt.</p>
        </div>
        {counterInfo && (
          <div className="rounded-xl border border-border bg-muted/30 px-4 py-3 min-w-[280px]" data-testid="invoice-counter-widget">
            <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-1">Rechnungs-Zähler {counterInfo.year}</div>
            <div className="text-sm">
              Nächste Rechnung: <span className="font-bold text-primary">{counterInfo.next_number}</span>
              <span className="text-muted-foreground"> · aktuell seq={counterInfo.current_seq}</span>
            </div>
            <div className="flex items-center gap-2 mt-2">
              <input
                type="number"
                min="0"
                value={counterInput}
                onChange={e=>setCounterInput(e.target.value)}
                placeholder={`z.B. ${counterInfo.current_seq}`}
                className="border rounded px-2 py-1 text-sm w-24"
                data-testid="counter-input"/>
              <Button size="sm" variant="outline" className="rounded-lg text-xs h-8"
                      onClick={setCounter} data-testid="set-counter-btn"
                      disabled={counterInput === ""}>
                Zähler setzen
              </Button>
            </div>
            <div className="text-[10px] text-muted-foreground mt-1.5">
              Tipp: seq=<b>26</b> → nächste = <b>{counterInfo.year}-27</b>
            </div>
          </div>
        )}
      </div>

      <div className="msb-card p-0 overflow-x-auto">
        <table className="w-full min-w-[800px]">
          <thead className="bg-muted/60">
            <tr className="text-xs uppercase tracking-widest text-muted-foreground">
              <th className="text-left p-4">Nr.</th>
              <th className="text-left p-4">Datum</th>
              <th className="text-left p-4">Empfänger</th>
              <th className="text-left p-4">Zeitraum</th>
              <th className="text-left p-4">Betrag</th>
              <th className="text-left p-4">Status</th>
              <th className="text-right p-4">Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(i => (
              <tr key={i.id} className="border-t border-border hover:bg-muted/40" data-testid={`invoice-row-${i.id}`}>
                <td className="p-4 font-mono text-sm font-semibold">{i.number}</td>
                <td className="p-4 text-sm">{formatDate(i.invoice_date)}</td>
                <td className="p-4 text-sm">
                  <div className="font-medium">{i.guest_name}</div>
                  {i.company && <div className="text-xs text-muted-foreground">{i.company}</div>}
                </td>
                <td className="p-4 text-sm">{formatDate(i.check_in)} → {formatDate(i.check_out)}</td>
                <td className="p-4 font-semibold">{euro(i.gross)}</td>
                <td className="p-4"><span className={`status-pill ${STATUS_COLORS[i.status]||"bg-slate-100"}`}>{i.status}</span></td>
                <td className="p-4 text-right whitespace-nowrap">
                  <Button size="sm" variant="outline" className="rounded-lg mr-2" onClick={()=>setPreview(i)} data-testid={`preview-invoice-${i.id}`}>
                    <Eye size={14}/> <span className="ml-1">Vorschau</span>
                  </Button>
                  <Button size="sm" variant="outline" className="rounded-lg mr-2 border-amber-300 text-amber-800 hover:bg-amber-50"
                          onClick={()=>setEditInv(i)} data-testid={`edit-invoice-${i.id}`}>
                    <PencilSimple size={14}/> <span className="ml-1">Ändern</span>
                  </Button>
                  <a href={`${API}/invoices/${i.id}/pdf`} target="_blank" rel="noreferrer">
                    <Button size="sm" variant="outline" className="rounded-lg mr-2" data-testid={`download-invoice-${i.id}`}>
                      <DownloadSimple size={14}/> <span className="ml-1">PDF</span>
                    </Button>
                  </a>
                  <Button size="sm" className="rounded-lg bg-primary" onClick={()=>openSendPreview(i)} data-testid={`send-invoice-${i.id}`}>
                    <PaperPlaneTilt size={14}/> <span className="ml-1">Senden</span>
                  </Button>
                  <Button size="sm" variant="outline" className="rounded-lg ml-2 text-red-700 border-red-200 hover:bg-red-50"
                          onClick={()=>remove(i)} data-testid={`delete-invoice-${i.id}`}>
                    <Trash size={14}/>
                  </Button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={7} className="p-8 text-center text-muted-foreground">Noch keine Rechnungen. Erstellen Sie eine aus einer Buchung heraus.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {preview && <InvoicePreview inv={preview} onClose={()=>setPreview(null)} />}
      {editInv && <EditInvoiceDialog inv={editInv} onClose={()=>setEditInv(null)} onSaved={()=>{ setEditInv(null); load(); }} />}
      {sendPreview && (
        <Dialog open={true} onOpenChange={(o)=>{ if (!o) { setSendPreview(null); setEditMode(false); } }}>
          <DialogContent className="max-w-4xl max-h-[92vh] overflow-hidden flex flex-col" data-testid="send-preview-dialog">
            <DialogHeader>
              <DialogTitle>Rechnung {sendPreview.invoice.number} · Prüfen & Versenden</DialogTitle>
            </DialogHeader>
            {/* Tab-Wechsel */}
            <div className="flex gap-1 border-b border-border -mx-6 px-6" data-testid="preview-tabs">
              {[
                { v: "invoice", label: "1. Rechnung prüfen & bearbeiten" },
                { v: "email",   label: "2. E-Mail-Vorschau" },
              ].map(t => (
                <button key={t.v}
                        type="button"
                        onClick={()=>setActiveTab(t.v)}
                        className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition ${
                          activeTab === t.v ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"
                        }`}
                        data-testid={`tab-${t.v}`}>
                  {t.label}
                </button>
              ))}
            </div>

            {activeTab === "invoice" && invEdit && (
              <div className="flex-1 overflow-auto pr-2 space-y-4" data-testid="tab-panel-invoice">
                <div className="flex flex-wrap items-center justify-between gap-2 pt-3">
                  <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-semibold">
                    Rechnungsempfänger & Details
                  </div>
                  <a href={`${API}/invoices/${sendPreview.invoice.id}/pdf?v=${pdfBust}`}
                     target="_blank" rel="noopener noreferrer"
                     className="text-xs px-3 py-1.5 rounded-lg border border-primary/30 bg-primary/5 text-primary hover:bg-primary/10 inline-flex items-center gap-1.5"
                     data-testid="invoice-pdf-preview">
                    <FilePdf size={14}/> PDF anzeigen
                  </a>
                </div>

                <fieldset className="border border-border rounded-xl p-3 space-y-2.5">
                  <legend className="px-2 text-[11px] uppercase tracking-widest text-muted-foreground font-semibold">Empfänger</legend>
                  <div className="grid grid-cols-2 gap-2.5 text-sm">
                    <div>
                      <label className="text-xs text-muted-foreground">Firma</label>
                      <input value={invEdit.company} onChange={e=>setInvEdit({...invEdit, company: e.target.value})}
                             className="w-full border rounded-lg px-2 py-1.5 mt-1" data-testid="inv-edit-company"/>
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground">z.Hd.</label>
                      <input value={invEdit.attention} onChange={e=>setInvEdit({...invEdit, attention: e.target.value})}
                             className="w-full border rounded-lg px-2 py-1.5 mt-1" data-testid="inv-edit-attention"/>
                    </div>
                    <div className="col-span-2">
                      <label className="text-xs text-muted-foreground">Name auf Rechnung</label>
                      <input value={invEdit.guest_name} onChange={e=>setInvEdit({...invEdit, guest_name: e.target.value})}
                             className="w-full border rounded-lg px-2 py-1.5 mt-1" data-testid="inv-edit-name"/>
                    </div>
                    <div className="col-span-2">
                      <label className="text-xs text-muted-foreground">Adresse (Straße, PLZ Ort, Land)</label>
                      <input value={invEdit.address} onChange={e=>setInvEdit({...invEdit, address: e.target.value})}
                             className="w-full border rounded-lg px-2 py-1.5 mt-1" data-testid="inv-edit-address"/>
                    </div>
                  </div>
                </fieldset>

                <fieldset className="border border-border rounded-xl p-3 space-y-2.5">
                  <legend className="px-2 text-[11px] uppercase tracking-widest text-muted-foreground font-semibold">Leistung & Termine</legend>
                  <div className="grid grid-cols-2 gap-2.5 text-sm">
                    <div>
                      <label className="text-xs text-muted-foreground">Rechnungsdatum</label>
                      <input type="date" value={invEdit.invoice_date} onChange={e=>setInvEdit({...invEdit, invoice_date: e.target.value})}
                             className="w-full border rounded-lg px-2 py-1.5 mt-1" data-testid="inv-edit-date"/>
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground">Fällig bis</label>
                      <input type="date" value={invEdit.due_date} onChange={e=>setInvEdit({...invEdit, due_date: e.target.value})}
                             className="w-full border rounded-lg px-2 py-1.5 mt-1" data-testid="inv-edit-due"/>
                    </div>
                    <div className="col-span-2">
                      <label className="text-xs text-muted-foreground">Leistungs-Beschreibung</label>
                      <textarea rows={2} value={invEdit.description} onChange={e=>setInvEdit({...invEdit, description: e.target.value})}
                                className="w-full border rounded-lg px-2 py-1.5 mt-1 text-sm" data-testid="inv-edit-description"/>
                    </div>
                  </div>
                </fieldset>

                <fieldset className="border border-border rounded-xl p-3 space-y-2.5">
                  <legend className="px-2 text-[11px] uppercase tracking-widest text-muted-foreground font-semibold">Zahlung</legend>
                  <div className="text-sm">
                    <label className="text-xs text-muted-foreground">Zahlungshinweis (auf der PDF)</label>
                    <textarea rows={3} value={invEdit.payment_note} onChange={e=>setInvEdit({...invEdit, payment_note: e.target.value})}
                              className="w-full border rounded-lg px-2 py-1.5 mt-1 font-mono text-xs"
                              placeholder="Leer = Standard-Text mit Betrag + Fälligkeit"
                              data-testid="inv-edit-paynote"/>
                    <div className="text-[10px] text-muted-foreground mt-1">
                      Konto (Inhaber, IBAN, BIC, Verwendungszweck) wird automatisch unter dem Text ergänzt.
                    </div>
                  </div>
                </fieldset>

                {sendPreview.invoice.items?.length > 0 && (
                  <fieldset className="border border-border rounded-xl p-3">
                    <legend className="px-2 text-[11px] uppercase tracking-widest text-muted-foreground font-semibold">
                      Positionen ({sendPreview.invoice.items.length}) · Gesamt {euro(sendPreview.invoice.gross)}
                    </legend>
                    <div className="text-xs text-muted-foreground -mt-1 mb-2">
                      Positionen können hier nicht direkt bearbeitet werden. Änderungen bitte über den „Extras"-Dialog der Buchung.
                    </div>
                    <div className="space-y-1 text-sm">
                      {sendPreview.invoice.items.map((it, i) => (
                        <div key={i} className="flex justify-between border-b border-dashed border-border py-1 last:border-0">
                          <span className="text-muted-foreground truncate mr-2">{it.description || it.label}</span>
                          <span className="font-medium whitespace-nowrap">{euro(it.total ?? it.amount)}</span>
                        </div>
                      ))}
                    </div>
                  </fieldset>
                )}

                <div className="flex justify-between items-center sticky bottom-0 bg-background pt-2">
                  <div className="text-[11px] text-muted-foreground">
                    💡 Deine Änderungen werden beim Klick auf „Weiter" oder „Senden" automatisch gespeichert.
                  </div>
                  <Button onClick={saveInvoice} disabled={savingInv} variant="outline" className="rounded-xl gap-1.5" data-testid="inv-edit-save">
                    <FloppyDisk size={14}/> {savingInv ? "Speichere…" : "Jetzt speichern"}
                  </Button>
                </div>
              </div>
            )}

            {activeTab === "email" && (
              <div className="space-y-3 flex-1 overflow-auto pr-2 pt-3" data-testid="tab-panel-email">
              <div className="grid grid-cols-[80px_1fr] gap-y-1.5 text-sm items-center">
                <div className="text-muted-foreground">An:</div>
                <div className="font-medium" data-testid="send-preview-to">{sendPreview.to || "— (keine Gast-E-Mail)"}</div>
                <div className="text-muted-foreground">Kopie:</div>
                <div className="font-medium text-emerald-700" data-testid="send-preview-bcc">📎 {sendPreview.cc || sendPreview.bcc || "—"}</div>
                <div className="text-muted-foreground">Betreff:</div>
                {editMode ? (
                  <input type="text"
                         value={sendPreview.subject}
                         onChange={e=>setSendPreview(s=>({...s, subject: e.target.value}))}
                         className="border rounded px-2 py-1 text-sm w-full"
                         data-testid="edit-subject"/>
                ) : (
                  <div className="font-medium">{sendPreview.subject}</div>
                )}
              </div>
              <div className="flex items-center justify-between border-t pt-3">
                <div className="text-[11px] uppercase tracking-widest text-muted-foreground">
                  {editMode ? "HTML bearbeiten" : "Vorschau"}
                </div>
                <button
                  className={`text-xs px-3 py-1.5 rounded-md border transition ${editMode ? "bg-amber-50 border-amber-300 text-amber-800 hover:bg-amber-100" : "bg-primary/5 border-primary/30 text-primary hover:bg-primary/10"}`}
                  onClick={()=>setEditMode(v=>!v)}
                  data-testid="toggle-edit-mode">
                  {editMode ? "◀ Vorschau anzeigen" : "✏️ E-Mail bearbeiten"}
                </button>
              </div>
              {editMode ? (
                <div className="space-y-2">
                  <textarea
                    value={sendPreview.html}
                    onChange={e=>setSendPreview(s=>({...s, html: e.target.value}))}
                    className="w-full min-h-[300px] font-mono text-xs border rounded-lg p-3 bg-amber-50/30 focus:outline-none focus:ring-2 focus:ring-amber-300"
                    data-testid="edit-html"/>
                  <div className="text-[11px] text-muted-foreground">
                    💡 Du bearbeitest den HTML-Quelltext. Text zwischen den Tags kannst du frei ändern. Vor Absenden auf „Vorschau anzeigen" klicken, um zu prüfen wie es beim Gast ankommt.
                  </div>
                </div>
              ) : (
                <div className="border rounded-lg overflow-hidden">
                  <div className="bg-muted/40 px-3 py-1.5 text-[11px] uppercase tracking-widest text-muted-foreground">Nachrichten-Vorschau</div>
                  <div className="p-4 bg-white" dangerouslySetInnerHTML={{__html: sendPreview.html}} data-testid="send-preview-html"/>
                </div>
              )}
              </div>
            )}
            <DialogFooter className="mt-3">
              <Button variant="outline" onClick={()=>{ setSendPreview(null); setEditMode(false); }} className="rounded-xl">Abbrechen</Button>
              {activeTab === "invoice" ? (
                <Button onClick={gotoEmailTab} className="rounded-xl bg-primary" data-testid="goto-email-tab">
                  Weiter zur E-Mail-Vorschau →
                </Button>
              ) : (
              <Button onClick={confirmSend} className="rounded-xl bg-primary" data-testid="confirm-send-invoice"
                      disabled={!sendPreview.to}>
                <PaperPlaneTilt size={14} className="mr-1"/>
                Jetzt an {sendPreview.to || "…"} senden
              </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

function EditInvoiceDialog({ inv, onClose, onSaved }) {
  // Vollständige Rechnungs-Bearbeitung — gleiche Rechnungsnummer, PDF wird neu generiert.
  const [form, setForm] = useState(() => ({
    company:   inv.company   || "",
    attention: inv.attention || "",
    guest_name:  inv.guest_name  || "",
    guest_names: inv.guest_names || "",
    address:   inv.address || "",
    description: inv.description || "",
    invoice_date: inv.invoice_date || "",
    due_date:  inv.due_date  || "",
    payment_note: inv.payment_note || "",
    paid_via_platform: inv.paid_via_platform ?? null,
    paid_platform_label: inv.paid_platform_label || "",
    items: (inv.items || []).map(it => ({
      label: it.label || "",
      qty:   Number(it.qty || 0),
      unit:  it.unit || "Nächte",
      price: Number(it.price || 0),
      total: Number(it.total || 0),
    })),
    gross: Number(inv.gross || 0),
    vat_rate: Number(inv.vat_rate || 7),
  }));
  const [saving, setSaving] = useState(false);
  const [pdfBust, setPdfBust] = useState(0);

  const recomputeItemTotal = (idx) => {
    setForm(f => {
      const items = [...f.items];
      const it = { ...items[idx] };
      it.total = Math.round(Number(it.qty || 0) * Number(it.price || 0) * 100) / 100;
      items[idx] = it;
      const sum = Math.round(items.reduce((a, x) => a + Number(x.total || 0), 0) * 100) / 100;
      return { ...f, items, gross: sum };
    });
  };

  const addItem = () => setForm(f => ({ ...f, items: [...f.items, { label: "Zusatz", qty: 1, unit: "Stk.", price: 0, total: 0 }] }));
  const removeItem = (idx) => setForm(f => {
    const items = f.items.filter((_, i) => i !== idx);
    const sum = Math.round(items.reduce((a, x) => a + Number(x.total || 0), 0) * 100) / 100;
    return { ...f, items, gross: sum };
  });

  const vatRate = Number(form.vat_rate) || 7;
  const net = Math.round(Number(form.gross) / (1 + vatRate / 100) * 100) / 100;
  const vat = Math.round((Number(form.gross) - net) * 100) / 100;

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        items: form.items.map(it => ({
          ...it,
          qty: Number(it.qty) || 0,
          price: Number(it.price) || 0,
          total: Number(it.total) || 0,
        })),
        gross: Number(form.gross) || 0,
        vat_rate: vatRate,
      };
      await api.patch(`/invoices/${inv.id}`, payload);
      toast.success(`Rechnung ${inv.number} aktualisiert · neues PDF bereit`);
      setPdfBust(Date.now());
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={(o)=>{ if (!o) onClose(); }}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-hidden flex flex-col" data-testid="edit-invoice-dialog">
        <DialogHeader>
          <DialogTitle>
            Rechnung {inv.number} bearbeiten
            <span className="ml-2 text-xs font-normal text-muted-foreground">
              · gleiche Rechnungsnummer, PDF wird neu generiert
            </span>
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-auto pr-2 space-y-4 pt-2">
          <div className="flex justify-end">
            <a href={`${API}/invoices/${inv.id}/pdf?v=${pdfBust}`}
               target="_blank" rel="noopener noreferrer"
               className="text-xs px-3 py-1.5 rounded-lg border border-primary/30 bg-primary/5 text-primary hover:bg-primary/10 inline-flex items-center gap-1.5"
               data-testid="edit-pdf-preview">
              <FilePdf size={14}/> Aktuelles PDF anzeigen
            </a>
          </div>

          <fieldset className="border border-border rounded-xl p-3 space-y-2.5">
            <legend className="px-2 text-[11px] uppercase tracking-widest text-muted-foreground font-semibold">Empfänger</legend>
            <div className="grid grid-cols-2 gap-2.5 text-sm">
              <div>
                <label className="text-xs text-muted-foreground">Firma</label>
                <input value={form.company} onChange={e=>setForm({...form, company: e.target.value})}
                       className="w-full border rounded-lg px-2 py-1.5 mt-1" data-testid="edit-company"/>
              </div>
              <div>
                <label className="text-xs text-muted-foreground">z.Hd.</label>
                <input value={form.attention} onChange={e=>setForm({...form, attention: e.target.value})}
                       className="w-full border rounded-lg px-2 py-1.5 mt-1" data-testid="edit-attention"/>
              </div>
              <div className="col-span-2">
                <label className="text-xs text-muted-foreground">Name auf Rechnung</label>
                <input value={form.guest_name} onChange={e=>setForm({...form, guest_name: e.target.value})}
                       className="w-full border rounded-lg px-2 py-1.5 mt-1" data-testid="edit-name"/>
              </div>
              <div className="col-span-2">
                <label className="text-xs text-muted-foreground">Namen der Gäste (optional, erscheint im Fließtext)</label>
                <input value={form.guest_names} onChange={e=>setForm({...form, guest_names: e.target.value})}
                       className="w-full border rounded-lg px-2 py-1.5 mt-1" data-testid="edit-guest-names"/>
              </div>
              <div className="col-span-2">
                <label className="text-xs text-muted-foreground">Adresse (Straße, PLZ Ort, Land)</label>
                <input value={form.address} onChange={e=>setForm({...form, address: e.target.value})}
                       className="w-full border rounded-lg px-2 py-1.5 mt-1" data-testid="edit-address"/>
              </div>
            </div>
          </fieldset>

          <fieldset className="border border-border rounded-xl p-3 space-y-2.5">
            <legend className="px-2 text-[11px] uppercase tracking-widest text-muted-foreground font-semibold">Leistung & Termine</legend>
            <div className="grid grid-cols-2 gap-2.5 text-sm">
              <div>
                <label className="text-xs text-muted-foreground">Rechnungsdatum</label>
                <input type="date" value={form.invoice_date} onChange={e=>setForm({...form, invoice_date: e.target.value})}
                       className="w-full border rounded-lg px-2 py-1.5 mt-1" data-testid="edit-date"/>
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Fällig bis</label>
                <input type="date" value={form.due_date} onChange={e=>setForm({...form, due_date: e.target.value})}
                       className="w-full border rounded-lg px-2 py-1.5 mt-1" data-testid="edit-due"/>
              </div>
              <div className="col-span-2">
                <label className="text-xs text-muted-foreground">Leistungs-Beschreibung</label>
                <textarea rows={2} value={form.description} onChange={e=>setForm({...form, description: e.target.value})}
                          className="w-full border rounded-lg px-2 py-1.5 mt-1 text-sm" data-testid="edit-description"/>
              </div>
            </div>
          </fieldset>

          <fieldset className="border border-border rounded-xl p-3">
            <legend className="px-2 text-[11px] uppercase tracking-widest text-muted-foreground font-semibold">
              Positionen · {form.items.length}
            </legend>
            <div className="space-y-2">
              {form.items.map((it, idx) => (
                <div key={idx} className="grid grid-cols-[1fr_60px_80px_90px_90px_32px] gap-1.5 items-end text-sm">
                  <div>
                    {idx === 0 && <label className="text-[10px] text-muted-foreground block">Bezeichnung</label>}
                    <input value={it.label}
                           onChange={e=>setForm(f=>{ const items=[...f.items]; items[idx]={...it, label:e.target.value}; return {...f, items}; })}
                           className="w-full border rounded px-2 py-1" data-testid={`edit-item-label-${idx}`}/>
                  </div>
                  <div>
                    {idx === 0 && <label className="text-[10px] text-muted-foreground block">Menge</label>}
                    <input type="number" step="1" value={it.qty}
                           onChange={e=>setForm(f=>{ const items=[...f.items]; items[idx]={...it, qty:e.target.value}; return {...f, items}; })}
                           onBlur={()=>recomputeItemTotal(idx)}
                           className="w-full border rounded px-2 py-1 text-right" data-testid={`edit-item-qty-${idx}`}/>
                  </div>
                  <div>
                    {idx === 0 && <label className="text-[10px] text-muted-foreground block">Einheit</label>}
                    <input value={it.unit}
                           onChange={e=>setForm(f=>{ const items=[...f.items]; items[idx]={...it, unit:e.target.value}; return {...f, items}; })}
                           className="w-full border rounded px-2 py-1" data-testid={`edit-item-unit-${idx}`}/>
                  </div>
                  <div>
                    {idx === 0 && <label className="text-[10px] text-muted-foreground block">Einzelpreis</label>}
                    <input type="number" step="0.01" value={it.price}
                           onChange={e=>setForm(f=>{ const items=[...f.items]; items[idx]={...it, price:e.target.value}; return {...f, items}; })}
                           onBlur={()=>recomputeItemTotal(idx)}
                           className="w-full border rounded px-2 py-1 text-right" data-testid={`edit-item-price-${idx}`}/>
                  </div>
                  <div>
                    {idx === 0 && <label className="text-[10px] text-muted-foreground block">Gesamt €</label>}
                    <input type="number" step="0.01" value={it.total}
                           onChange={e=>setForm(f=>{
                              const items=[...f.items]; items[idx]={...it, total:e.target.value};
                              const sum = Math.round(items.reduce((a,x)=>a+Number(x.total||0),0)*100)/100;
                              return {...f, items, gross: sum};
                           })}
                           className="w-full border rounded px-2 py-1 text-right font-semibold bg-amber-50" data-testid={`edit-item-total-${idx}`}/>
                  </div>
                  <button type="button" onClick={()=>removeItem(idx)}
                          className="text-red-600 hover:bg-red-50 rounded h-7 w-7 flex items-center justify-center"
                          title="Position löschen"
                          data-testid={`edit-item-del-${idx}`}>
                    <X size={14}/>
                  </button>
                </div>
              ))}
              <button type="button" onClick={addItem}
                      className="text-xs text-primary hover:underline flex items-center gap-1 mt-2"
                      data-testid="edit-add-item">
                <Plus size={12}/> Position hinzufügen
              </button>
            </div>
            <div className="text-[11px] text-muted-foreground mt-2 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
              💡 Tipp: Menge × Einzelpreis wird bei Verlassen des Feldes automatisch zum Gesamtbetrag berechnet.
              Du kannst den Gesamtbetrag (gelbes Feld) auch direkt überschreiben, falls du z.B. nur die Centstellen korrigieren willst.
            </div>
          </fieldset>

          <fieldset className="border border-border rounded-xl p-3">
            <legend className="px-2 text-[11px] uppercase tracking-widest text-muted-foreground font-semibold">Summen</legend>
            <div className="grid grid-cols-3 gap-3 text-sm">
              <div>
                <label className="text-xs text-muted-foreground">Netto</label>
                <div className="font-semibold mt-1 text-right">{euro(net)}</div>
              </div>
              <div>
                <label className="text-xs text-muted-foreground">+{vatRate}% MwSt.</label>
                <div className="font-semibold mt-1 text-right">{euro(vat)}</div>
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Bruttobetrag (überschreibbar)</label>
                <input type="number" step="0.01" value={form.gross}
                       onChange={e=>setForm({...form, gross: e.target.value})}
                       className="w-full border-2 border-primary rounded-lg px-2 py-1.5 mt-1 text-right font-bold text-lg text-primary bg-primary/5"
                       data-testid="edit-gross"/>
              </div>
            </div>
            <div className="text-[11px] text-muted-foreground mt-2">
              Netto & MwSt werden automatisch aus Brutto und MwSt-Satz berechnet. Falls du einen Centstellen-Dreher korrigieren willst — einfach direkt in das große Feld rechts tippen.
            </div>
          </fieldset>

          <fieldset className="border border-border rounded-xl p-3">
            <legend className="px-2 text-[11px] uppercase tracking-widest text-muted-foreground font-semibold">Zahlung</legend>
            <div className="space-y-2 text-sm">
              <label className="flex items-center gap-2">
                <input type="checkbox"
                       checked={form.paid_via_platform === true}
                       onChange={e=>setForm({...form, paid_via_platform: e.target.checked ? true : null})}
                       data-testid="edit-paid-platform"/>
                <span>Bereits über Plattform bezahlt (Booking.com / Airbnb) — zeigt "beglichen" statt Überweisungstext</span>
              </label>
              {form.paid_via_platform && (
                <input value={form.paid_platform_label}
                       onChange={e=>setForm({...form, paid_platform_label: e.target.value})}
                       placeholder="z.B. Booking.com"
                       className="w-full border rounded-lg px-2 py-1.5 ml-6" data-testid="edit-paid-platform-label"/>
              )}
              <div>
                <label className="text-xs text-muted-foreground">Zahlungshinweis (optional, überschreibt Standardtext)</label>
                <textarea rows={2} value={form.payment_note} onChange={e=>setForm({...form, payment_note: e.target.value})}
                          className="w-full border rounded-lg px-2 py-1.5 mt-1 font-mono text-xs"
                          placeholder="Leer = Standard-Text mit Betrag + Fälligkeit"
                          data-testid="edit-paynote"/>
              </div>
            </div>
          </fieldset>
        </div>

        <DialogFooter className="mt-3 pt-3 border-t">
          <Button variant="outline" onClick={onClose} className="rounded-xl" data-testid="edit-cancel">Abbrechen</Button>
          <Button onClick={save} disabled={saving} className="rounded-xl bg-primary" data-testid="edit-save">
            <FloppyDisk size={14} className="mr-1"/>
            {saving ? "Speichere…" : "Speichern & PDF aktualisieren"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function InvoicePreview({ inv, onClose }) {
  const source = (inv.source || "").toLowerCase();
  const external = source.includes("booking") || source.includes("airbnb");
  const platform = source.includes("booking") ? "booking.com" : "Airbnb";
  const items = inv.items?.length ? inv.items : [{
    label: inv.room_name || "Übernachtung",
    qty: inv.nights, unit: "Nächte",
    price: inv.price_per_night, total: inv.gross,
  }];

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-6" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-auto" onClick={e=>e.stopPropagation()} data-testid="invoice-preview">
        <div className="p-10 font-[Helvetica] text-[13px] text-slate-800">
          {/* Header */}
          <div className="flex justify-between items-start">
            <div>
              <div className="text-3xl font-bold text-primary" style={{fontFamily:"'Work Sans',sans-serif"}}>MainSpessartBlick</div>
              <div className="mt-3 leading-6">
                <div>Ivan Giombini</div>
                <div>Ringstraße 22b</div>
                <div>97816 Lohr am Main</div>
                <div>Telefon: +49 176 25799945</div>
                <div>E-Mail: info@mainspessartblick.de</div>
                <div>Steuernummer: 231/220/80307</div>
              </div>
            </div>
            <div className="text-3xl font-bold text-primary" style={{fontFamily:"'Work Sans',sans-serif"}}>Rechnung</div>
          </div>

          {/* Adressblock */}
          <div className="grid grid-cols-2 gap-8 mt-10">
            <div>
              <div className="font-bold">Rechnung an:</div>
              {inv.company && <div>{inv.company}</div>}
              {inv.attention && <div>z.Hd. {inv.attention}</div>}
              {!inv.company && <div>{inv.guest_name}</div>}
              {(inv.address || "").split(",").map(p=>p.trim()).filter(Boolean).map((p,idx)=>(
                <div key={idx}>{p}</div>
              ))}
            </div>
            <div className="text-right leading-6">
              <div className="font-bold">Rechnungsnummer: {inv.number}</div>
              <div>Rechnungsdatum: {formatDate(inv.invoice_date)}</div>
              <div>Leistungszeitraum: {dePeriod(inv.check_in, inv.check_out)}</div>
            </div>
          </div>

          {/* Beschreibung */}
          <div className="mt-8">
            <div className="font-bold">Beschreibung der Leistung:</div>
            <div className="mt-1">{inv.description || `Reservierung ${inv.room_name}`}</div>
            {inv.guest_names && <>
              <div className="mt-3">Name der Gäste:</div>
              <div>{inv.guest_names}</div>
            </>}
          </div>

          {/* Tabelle */}
          <table className="w-full mt-6 text-sm">
            <thead className="bg-slate-100 text-slate-700 text-xs uppercase">
              <tr>
                <th className="text-left p-2">Bezeichnung</th>
                <th className="text-right p-2">Menge</th>
                <th className="text-right p-2">Einzelpreis</th>
                <th className="text-right p-2">Gesamtbetrag</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it, idx) => (
                <tr key={idx} className="border-b">
                  <td className="p-2">{it.label}</td>
                  <td className="p-2 text-right">{it.qty} {it.unit}</td>
                  <td className="p-2 text-right">{euro(it.price)}</td>
                  <td className="p-2 text-right">{euro(it.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Summen */}
          <div className="mt-4 text-right space-y-1">
            <div className="text-lg font-bold text-primary" style={{fontFamily:"'Work Sans',sans-serif"}}>
              Gesamtbetrag (inkl. {parseInt(inv.vat_rate)}% MwSt.): {euro(inv.gross)}
            </div>
            <div className="text-sm">Enthaltene MwSt. ({parseInt(inv.vat_rate)}%): {euro(inv.vat)}</div>
          </div>

          {/* Zahlung */}
          <div className="mt-8">
            <div className="font-bold">Zahlung</div>
            {external ? (
              <div className="mt-1 whitespace-pre-line" data-testid="invoice-preview-payment-note">{(inv.payment_note || "").trim() || `Der Rechnungsbetrag wurde bereits über ${platform} beglichen.`}</div>
            ) : (
              <div className="mt-1 space-y-1">
                <div className="whitespace-pre-line">
                  {(inv.payment_note || "").trim() || <>Wir bitten Sie, den Betrag in Höhe von <strong>{euro(inv.gross)}</strong> bis spätestens {formatDate(inv.due_date || inv.check_in)} auf folgendes Konto zu überweisen:</>}
                </div>
                <div className="pt-2">Kontoinhaber: Ivan Giombini</div>
                <div>IBAN: DE53 7905 0000 0049 6207 01</div>
                <div>BIC: BYLADEM1SWU</div>
                <div>Verwendungszweck: Buchung MainSpessartBlick {inv.number}</div>
              </div>
            )}
          </div>

          {/* Ort + Fußzeile */}
          <div className="mt-8">Lohr am Main, {formatDate(inv.invoice_date)}</div>
          <div className="mt-10 pt-6 border-t text-xs text-muted-foreground italic text-center">
            Diese Rechnung wurde maschinell erstellt und ist auch ohne Unterschrift gültig.
          </div>

          <div className="flex justify-end mt-6 gap-2">
            <a href={`${API}/invoices/${inv.id}/pdf`} target="_blank" rel="noreferrer">
              <Button variant="outline" className="rounded-xl"><DownloadSimple size={14}/> <span className="ml-1">PDF öffnen</span></Button>
            </a>
            <Button onClick={onClose} className="rounded-xl">Schließen</Button>
          </div>
        </div>
      </div>
    </div>
  );
}
